import { Injectable } from '@angular/core';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import * as moment from 'moment';

// Cache version - increment when API response structure changes
const REPORT_CACHE_VERSION = 12; // Bumped: report includes now request map_feature_link (design context) on every linkable array
const PICTURE_CACHE_VERSION = 1; // Incremented for original_name field

interface DailyReportPicture {
  uuid: string;
  thumb_url: string;
  original_url: string;
  original_name?: string; // Added in version 2
  latitude: number | null;
  longitude: number | null;
  direction: number | null;
  site_uuid: string | null;
  site_name: string | null;
  report_uuid: string | null;
  report_date: string; // YYYY-MM-DD
  source_type: 'report_bid_item' | 'report_activity' | 'report_activity_code_header' | 'report_equipment' | 'report_labor' | 'report_material_delivery' | 'report_internal_test' | 'issue';
  source_uuid: string;
  source_name: string | null;
  created_at: string; // ISO 8601
}

interface ReportCacheEntry {
  version: number;
  projectId: string;
  reports: any[];
  fetchedAt: number;
  dateRange: {
    start: string; // YYYY-MM-DD
    end: string;   // YYYY-MM-DD
  };
  maxUpdatedAt?: string; // ISO 8601 - latest updated_at from all reports
}

interface PictureCacheEntry {
  version: number;
  projectId: string;
  pictures: DailyReportPicture[];
  fetchedAt: number;
  dateRange: {
    start: string;
    end: string;
  };
  siteIds?: string[]; // Track which sites were included
}

interface DailyReportCacheDB extends DBSchema {
  reports: {
    key: string; // projectId
    value: ReportCacheEntry;
  };
  pictures: {
    key: string; // projectId or projectId_siteIds
    value: PictureCacheEntry;
  };
}

@Injectable({
  providedIn: 'root'
})
export class DailyReportCacheService {
  private db: IDBPDatabase<DailyReportCacheDB>;
  private dbName = 'sitenotes_daily_reports';
  private dbReady: Promise<void>;

  // In-memory cache (fastest)
  private memoryCache = new Map<string, ReportCacheEntry>();
  private pictureMemoryCache = new Map<string, PictureCacheEntry>();

  constructor() {
    this.dbReady = this.initDB();
  }

  private async initDB(): Promise<void> {
    this.db = await openDB<DailyReportCacheDB>(this.dbName, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('reports')) {
          db.createObjectStore('reports', { keyPath: 'projectId' });
        }
        if (!db.objectStoreNames.contains('pictures')) {
          db.createObjectStore('pictures');
        }
      }
    });
  }

  /**
   * Get cached reports for date range
   * Returns null if cache miss or expired
   */
  async getCachedReports(
    projectId: string,
    startDate: string,
    endDate: string
  ): Promise<any[] | null> {
    // 1. Check memory cache first (instant)
    const memCache = this.memoryCache.get(projectId);
    if (memCache && this.isCacheValid(memCache, startDate, endDate)) {
      return this.filterReportsByDateRange(memCache.reports, startDate, endDate);
    }

    // 2. Check IndexedDB (fast - ~10ms)
    await this.dbReady; // Ensure DB is initialized
    const dbCache = await this.db.get('reports', projectId);

    // Check version - invalidate if outdated
    if (dbCache && dbCache.version !== REPORT_CACHE_VERSION) {
      await this.db.delete('reports', projectId);
      return null;
    }

    if (dbCache && this.isCacheValid(dbCache, startDate, endDate)) {
      // Promote to memory cache
      this.memoryCache.set(projectId, dbCache);
      return this.filterReportsByDateRange(dbCache.reports, startDate, endDate);
    }

    return null;
  }

  /**
   * Store reports in cache
   */
  async setCachedReports(
    projectId: string,
    reports: any[],
    startDate: string,
    endDate: string
  ): Promise<void> {
    // Compute maxUpdatedAt from all reports
    const maxUpdatedAt = this.computeMaxUpdatedAt(reports);

    const cacheEntry: ReportCacheEntry = {
      version: REPORT_CACHE_VERSION,
      projectId,
      reports,
      fetchedAt: Date.now(),
      dateRange: { start: startDate, end: endDate },
      maxUpdatedAt
    };

    // Store in both memory and IndexedDB
    this.memoryCache.set(projectId, cacheEntry);
    await this.dbReady; // Ensure DB is initialized
    await this.db.put('reports', cacheEntry);
  }

  /**
   * Compute the maximum updated_at timestamp from reports
   */
  private computeMaxUpdatedAt(reports: any[]): string | undefined {
    if (!reports || reports.length === 0) return undefined;

    let maxDate: Date | null = null;

    for (const report of reports) {
      const updatedAt = report.updated_at;
      if (updatedAt) {
        const date = new Date(updatedAt);
        if (!maxDate || date > maxDate) {
          maxDate = date;
        }
      }
    }

    return maxDate ? maxDate.toISOString() : undefined;
  }

  /**
   * Check if cached data is valid for requested range
   */
  private isCacheValid(
    cache: ReportCacheEntry,
    requestStart: string,
    requestEnd: string
  ): boolean {
    // Check if cache covers requested range
    const cacheCovers =
      cache.dateRange.start <= requestStart &&
      cache.dateRange.end >= requestEnd;

    if (!cacheCovers) {
      return false;
    }

    // Check if cache is fresh
    const age = Date.now() - cache.fetchedAt;
    const maxAge = this.getMaxCacheAge(requestStart, requestEnd);

    if (age > maxAge) {
      return false;
    }

    return true;
  }

  /**
   * Get max cache age based on date range
   * Recent data: 1 hour
   * Old data (>1 month ago): 24 hours
   */
  private getMaxCacheAge(startDate: string, endDate: string): number {
    const end = moment(endDate);
    const now = moment();
    const daysAgo = now.diff(end, 'days');

    if (daysAgo < 7) {
      return 60 * 60 * 1000; // 1 hour for recent data
    } else if (daysAgo < 30) {
      return 6 * 60 * 60 * 1000; // 6 hours for last month
    } else {
      return 24 * 60 * 60 * 1000; // 24 hours for old data
    }
  }

  /**
   * Filter reports by date range (client-side)
   */
  private filterReportsByDateRange(
    reports: any[],
    startDate: string,
    endDate: string
  ): any[] {
    return reports.filter(report => {
      const reportDate = report.report_date;
      return reportDate >= startDate && reportDate <= endDate;
    });
  }

  /**
   * Merge updated reports into existing cache (incremental update)
   * Updates existing reports and adds new ones, then recalculates maxUpdatedAt
   */
  async mergeUpdatedReports(projectId: string, updatedReports: any[]): Promise<void> {
    if (!updatedReports || updatedReports.length === 0) return;

    // Get existing cache
    await this.dbReady;
    const existingCache = this.memoryCache.get(projectId) || await this.db.get('reports', projectId);

    if (!existingCache) {
      // No existing cache - can't merge, need full fetch
      return;
    }

    // Create a map for fast lookup
    const reportsMap = new Map<string, any>();
    for (const report of existingCache.reports) {
      reportsMap.set(report.id, report);
    }

    // Merge updated reports (update existing, add new)
    for (const updatedReport of updatedReports) {
      reportsMap.set(updatedReport.id, updatedReport);
    }

    // Convert back to array
    const mergedReports = Array.from(reportsMap.values());

    // Recalculate maxUpdatedAt
    const newMaxUpdatedAt = this.computeMaxUpdatedAt(mergedReports);

    // Update cache entry
    const updatedCache: ReportCacheEntry = {
      ...existingCache,
      reports: mergedReports,
      maxUpdatedAt: newMaxUpdatedAt,
      fetchedAt: Date.now() // Update fetch time since we've refreshed
    };

    // Store in both memory and IndexedDB
    this.memoryCache.set(projectId, updatedCache);
    await this.db.put('reports', updatedCache);
  }

  /**
   * Clear cache for project
   */
  async clearCache(projectId: string): Promise<void> {
    this.memoryCache.delete(projectId);
    await this.dbReady; // Ensure DB is initialized
    await this.db.delete('reports', projectId);
  }

  /**
   * Get cache info for debugging
   */
  async getCacheInfo(projectId: string) {
    const memCache = this.memoryCache.get(projectId);
    await this.dbReady; // Ensure DB is initialized
    const dbCache = await this.db.get('reports', projectId);

    return {
      inMemory: memCache ? {
        reports: memCache.reports.length,
        age: (Date.now() - memCache.fetchedAt) / 1000 / 60 + ' min',
        range: memCache.dateRange,
        maxUpdatedAt: memCache.maxUpdatedAt
      } : null,
      inIndexedDB: dbCache ? {
        reports: dbCache.reports.length,
        age: (Date.now() - dbCache.fetchedAt) / 1000 / 60 + ' min',
        range: dbCache.dateRange,
        maxUpdatedAt: dbCache.maxUpdatedAt
      } : null
    };
  }

  /**
   * Get cached maxUpdatedAt for a project (used for staleness check)
   * Returns null if no cache exists
   */
  async getCachedMaxUpdatedAt(projectId: string): Promise<string | null> {
    // Check memory first
    const memCache = this.memoryCache.get(projectId);
    if (memCache?.maxUpdatedAt) {
      return memCache.maxUpdatedAt;
    }

    // Check IndexedDB
    await this.dbReady;
    const dbCache = await this.db.get('reports', projectId);
    return dbCache?.maxUpdatedAt || null;
  }

  // ============================================
  // PICTURE CACHING METHODS
  // ============================================

  /**
   * Get cached pictures for date range
   */
  async getCachedPictures(
    projectId: string,
    startDate: string,
    endDate: string,
    siteIds?: string[]
  ): Promise<DailyReportPicture[] | null> {
    // 1. Check memory cache
    const cacheKey = this.getPictureCacheKey(projectId, siteIds);
    const memCache = this.pictureMemoryCache.get(cacheKey);

    if (memCache && this.isPictureCacheValid(memCache, startDate, endDate)) {
      return this.filterPicturesByDateRange(memCache.pictures, startDate, endDate);
    }

    // 2. Check IndexedDB
    await this.dbReady; // Ensure DB is initialized
    const dbCache = await this.db.get('pictures', cacheKey);

    // Check version - invalidate if outdated
    if (dbCache && dbCache.version !== PICTURE_CACHE_VERSION) {
      await this.db.delete('pictures', cacheKey);
      return null;
    }

    if (dbCache && this.isPictureCacheValid(dbCache, startDate, endDate)) {
      this.pictureMemoryCache.set(cacheKey, dbCache);
      return this.filterPicturesByDateRange(dbCache.pictures, startDate, endDate);
    }

    return null;
  }

  /**
   * Store pictures in cache
   */
  async setCachedPictures(
    projectId: string,
    pictures: DailyReportPicture[],
    startDate: string,
    endDate: string,
    siteIds?: string[]
  ): Promise<void> {
    const cacheKey = this.getPictureCacheKey(projectId, siteIds);
    const cacheEntry: PictureCacheEntry = {
      version: PICTURE_CACHE_VERSION,
      projectId,
      pictures,
      fetchedAt: Date.now(),
      dateRange: { start: startDate, end: endDate },
      siteIds
    };

    this.pictureMemoryCache.set(cacheKey, cacheEntry);
    await this.dbReady; // Ensure DB is initialized
    await this.db.put('pictures', cacheEntry, cacheKey);
  }

  /**
   * Check if picture cache is valid
   * Pictures are very stable, so use longer cache times
   */
  private isPictureCacheValid(
    cache: PictureCacheEntry,
    requestStart: string,
    requestEnd: string
  ): boolean {
    // Check if cache covers requested range
    const cacheCovers =
      cache.dateRange.start <= requestStart &&
      cache.dateRange.end >= requestEnd;

    if (!cacheCovers) return false;

    // Check freshness
    const age = Date.now() - cache.fetchedAt;
    const maxAge = this.getPictureMaxCacheAge(requestStart, requestEnd);

    return age <= maxAge;
  }

  /**
   * Get max cache age for pictures
   * Pictures are very stable - use aggressive caching
   */
  private getPictureMaxCacheAge(startDate: string, endDate: string): number {
    const end = moment(endDate);
    const now = moment();
    const daysAgo = now.diff(end, 'days');

    if (daysAgo < 1) {
      return 30 * 60 * 1000; // 30 minutes for today
    } else if (daysAgo < 7) {
      return 2 * 60 * 60 * 1000; // 2 hours for last week
    } else if (daysAgo < 30) {
      return 12 * 60 * 60 * 1000; // 12 hours for last month
    } else {
      return 7 * 24 * 60 * 60 * 1000; // 7 days for old pictures
    }
  }

  /**
   * Generate cache key (includes siteIds for filtering)
   */
  private getPictureCacheKey(projectId: string, siteIds?: string[]): string {
    if (!siteIds || siteIds.length === 0) {
      return `${projectId}_all`;
    }
    // Sort and join for consistent key
    return `${projectId}_${siteIds.sort().join(',')}`;
  }

  /**
   * Filter pictures by date range (client-side)
   */
  private filterPicturesByDateRange(
    pictures: DailyReportPicture[],
    startDate: string,
    endDate: string
  ): DailyReportPicture[] {
    return pictures.filter(pic => {
      const picDate = pic.report_date;
      return picDate >= startDate && picDate <= endDate;
    });
  }

  /**
   * Clear picture cache
   */
  async clearPictureCache(projectId: string): Promise<void> {
    // Clear all picture cache entries for this project
    const keys = Array.from(this.pictureMemoryCache.keys())
      .filter(key => key.startsWith(projectId));

    keys.forEach(key => this.pictureMemoryCache.delete(key));

    // Clear from IndexedDB
    await this.dbReady; // Ensure DB is initialized
    const allKeys = await this.db.getAllKeys('pictures');
    const projectKeys = allKeys.filter(key => key.toString().startsWith(projectId));

    for (const key of projectKeys) {
      await this.db.delete('pictures', key);
    }
  }

  /**
   * Clear all caches for project (both reports and pictures)
   */
  async clearAllCaches(projectId: string): Promise<void> {
    await this.clearCache(projectId);
    await this.clearPictureCache(projectId);
  }
}
